import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, Building2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { api } from '../services/api';

const DefinirSenha = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    novaSenha: '',
    confirmarSenha: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    novaSenha: false,
    confirmarSenha: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Verificar se token e email estão presentes
    if (!token || !email) {
      navigate('/login');
    }
  }, [token, email, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.novaSenha) {
      newErrors.novaSenha = 'Nova senha é obrigatória';
    } else if (formData.novaSenha.length < 6) {
      newErrors.novaSenha = 'Nova senha deve ter pelo menos 6 caracteres';
    }

    if (!formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Confirmação de senha é obrigatória';
    } else if (formData.novaSenha !== formData.confirmarSenha) {
      newErrors.confirmarSenha = 'Senhas não coincidem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await api.post('/auth/definir-senha', {
        token,
        email,
        novaSenha: formData.novaSenha
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ geral: error.response?.data?.message || 'Erro ao definir senha' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="main-container flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
              Senha Definida com Sucesso!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Você será redirecionado para a página de login em instantes.
            </p>
          </div>
          
          <div className="text-center">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Building2 className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">
            Definir Senha
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Defina sua senha de acesso ao sistema
          </p>
          {email && (
            <p className="mt-1 text-center text-sm text-gray-500">
              {email}
            </p>
          )}
        </div>

        {errors.geral && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">
                  {errors.geral}
                </p>
              </div>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="novaSenha" className="block text-sm font-medium text-gray-700 mb-1">
                Nova Senha *
              </label>
              <div className="relative">
                <input
                  id="novaSenha"
                  type={showPasswords.novaSenha ? 'text' : 'password'}
                  name="novaSenha"
                  value={formData.novaSenha}
                  onChange={handleInputChange}
                  className={`input pr-10 ${errors.novaSenha ? 'border-red-300' : ''}`}
                  placeholder="Digite sua nova senha"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('novaSenha')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.novaSenha ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.novaSenha && (
                <p className="mt-1 text-sm text-red-600">{errors.novaSenha}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmarSenha" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmar Nova Senha *
              </label>
              <div className="relative">
                <input
                  id="confirmarSenha"
                  type={showPasswords.confirmarSenha ? 'text' : 'password'}
                  name="confirmarSenha"
                  value={formData.confirmarSenha}
                  onChange={handleInputChange}
                  className={`input pr-10 ${errors.confirmarSenha ? 'border-red-300' : ''}`}
                  placeholder="Confirme sua nova senha"
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirmarSenha')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPasswords.confirmarSenha ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmarSenha && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmarSenha}</p>
              )}
            </div>
          </div>

          <div className="text-xs text-gray-500">
            <p>Sua senha deve ter:</p>
            <ul className="list-disc list-inside mt-1">
              <li>Pelo menos 6 caracteres</li>
            </ul>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Definindo senha...
                </>
              ) : (
                'Definir Senha'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefinirSenha;

